import {
  formatDate,
  getNameValidationRules,
  getPhoneValidationRules,
  handlePaymentTermDown,
  handlePhoneKeyDown,
  handleTextValidation,
} from '@/utils'
import { Info, Loader } from 'lucide-react'
import { useEffect, useState } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { MessagesWithAds, UserData } from '../chat'
import SignaturePad from './SignaturePad'
import axios from 'axios'
import { API_URL } from '@/constant/api'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip'
import { jwtDecode, type JwtPayload } from 'jwt-decode'

interface GuestTokenPayload extends JwtPayload {
  name: string
  email: string
  userId: string
}
interface FormData {
  clientName: string
  clientAddress: string
  phoneNumber: string
  jobSiteAddress: string
  descriptionOfWork: string
  date: string
  startDate: Date | null
  proposalExpirationDate: Date | null
  estimatAmount: string
  paymentTerms: string
  logoFile: FileList | null
  customerSignatureFile: FileList | null
  contractorSignatureFile: FileList | null
  companyName: string
  roomdimensionFloorType: string
  companyPhoneNumber: string
  state: string
  city: string
  zipcode: string
}
interface PdfFormProps {
  setOpenPdfModal: (open: boolean) => void
  setMessagesWithAds: React.Dispatch<React.SetStateAction<MessagesWithAds[]>>
}
const PdfForm = ({ setOpenPdfModal, setMessagesWithAds }: PdfFormProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [contractSignature, setContractSignature] = useState<string>('')
  const [customerSignature, setCustomerSignature] = useState<string>('')
  const [opencustomerSignature, setOpenCustomerSignature] = useState(false)
  const [openContractSignature, setOpenContractSignature] = useState(false)
  const [startDate, setStartDate] = useState<Date | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    control,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      clientName: '',
      clientAddress: '',
      phoneNumber: '',
      jobSiteAddress: '',
      descriptionOfWork: '',
      date: '',
      startDate: null,
      proposalExpirationDate: null,
      estimatAmount: '',
      paymentTerms: '',
      companyName: '',
      roomdimensionFloorType: '',
      companyPhoneNumber: '',
      zipcode: '',
      state: '',
      city: '',
    },
    mode: 'onChange',
  })
  const [userData, setUserData] = useState<UserData | null>(null)
  useEffect(() => {
    const token = localStorage.getItem('guestToken')
    if (!token) return
    try {
      const decoded = jwtDecode<GuestTokenPayload>(token)
      const now = Date.now() / 1000
      const expiresIn = decoded?.exp || 0

      if (expiresIn < now) {
        localStorage.removeItem('guestToken')
        return
      }
      setUserData({
        name: decoded.name,
        email: decoded.email,
        userId: decoded.userId,
      })
    } catch {
      localStorage.removeItem('guestToken')
    }
  }, [])

  const onSubmit: SubmitHandler<FormData> = async data => {
    const formData = new FormData()
    const contractorFile = data.contractorSignatureFile?.[0]
    if (contractorFile) {
      const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/webp',
        'image/svg+xml',
      ]
      if (!allowedTypes.includes(contractorFile.type)) {
        setError('contractorSignatureFile', {
          type: 'manual',
          message: 'Only image files are allowed (jpg, png, webp, svg)',
        })
        return // STOP form submission
      }
      formData.append(
        'contractorSignatureFile',
        contractorFile,
        contractorFile.name
      )
    }
    const customerFile = data.customerSignatureFile?.[0]
    if (customerFile) {
      const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/webp',
        'image/svg+xml',
      ]
      if (!allowedTypes.includes(customerFile.type)) {
        setError('customerSignatureFile', {
          type: 'manual',
          message: 'Only images are allowed for Customer Signature',
        })

        return // stop submission
      }
      formData.append('customerSignatureFile', customerFile, customerFile.name)
    }
    formData.append('clientName', data.clientName)
    formData.append('clientAddress', data.clientAddress)
    formData.append('phoneNumber', data.phoneNumber)
    formData.append('companyPhoneNumber', data.companyPhoneNumber)
    formData.append('jobSiteAddress', data.jobSiteAddress)
    formData.append('descriptionOfWork', data.descriptionOfWork)
    formData.append('date', data.date)
    if (data.startDate) {
      formData.append('startDate', formatDate(data.startDate))
    }
    if (data.proposalExpirationDate) {
      formData.append(
        'proposalExpirationDate',
        formatDate(data.proposalExpirationDate)
      )
    }
    formData.append('estimatAmount', data.estimatAmount)
    formData.append('paymentTerms', data.paymentTerms)
    formData.append('companyName', data.companyName)
    formData.append('roomdimensionFloorType', data.roomdimensionFloorType)
    formData.append('contractorSignature', contractSignature)
    formData.append('customerSignature', customerSignature)
    formData.append('state', data.state)
    formData.append('city', data.city)
    formData.append('zipcode', data.zipcode)
    formData.append('userName', userData?.name ?? '')
    if (data.logoFile && data.logoFile.length > 0) {
      const logo = data.logoFile[0]
      if (logo) {
        formData.append('logoFile', logo, logo.name)
      }
    }

    if (userData) {
      formData.append('userId', userData?.email ?? '')
      formData.append('sessionId', userData?.userId ?? '')
    }

    try {
      setIsLoading(true)
      const res = await axios.post(`${API_URL}/estimate/generate`, formData)
      if (res.data.pdfURL) {
        setMessagesWithAds(prev => [
          ...prev,
          {
            userQuery: '',
            response:
              'Your estimation has been generated! You can download the PDF by clicking the button below.',
            pdfLink: res.data.pdfURL,
            type: 'message',
            timestamp: new Date().toISOString(),
          },
        ])
      }
      setIsLoading(false)
      setOpenPdfModal(false)
    } catch (error) {
      const errorMessage =
        axios.isAxiosError(error) && error.response?.data?.message
          ? error.response.data.message
          : 'Something went wrong!'
      setIsLoading(false)
      toast.error(errorMessage)
    }
  }

  return (
    <div className='hide-scroll overflow-y-auto w-full h-full max-h-[584px]'>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className='w-full bg-white space-y-8 overflow-hidden'
      >
        <div className='field-wrapper space-y-3 md:space-y-4'>
          <div className='input_field_col2'>
            <div className='input_field'>
              <label htmlFor='clientName' className=''>
                Client Name<span className='error-msg'>*</span>
                <span
                  data-tooltip-id='clientName'
                  data-tooltip-content='Name must be 2–20 characters'
                  className='inline-flex items-center relative top-1.5 ml-1'
                >
                  <Info width={15} />
                </span>
              </label>
              <Tooltip
                id='clientName'
                content='Client Name must be 2–20 characters'
              />
              <input
                id='clientName'
                type='text'
                {...register(
                  'clientName',
                  getNameValidationRules({ label: 'Client Name' })
                )}
                onKeyDown={handleTextValidation}
                className={`${errors.clientName ? 'border-red-500' : ''}`}
              />
              {errors.clientName && (
                <p className='error-msg'>{errors.clientName.message}</p>
              )}
            </div>
            <div className='input_field'>
              <label htmlFor='phoneNumber' className=''>
                Client Phone Number<span className='error-msg'>*</span>
                <span
                  data-tooltip-id='phoneNumber'
                  data-tooltip-content='Phone number must be 10–15 digit'
                  className='inline-flex items-center relative top-1.5 ml-1'
                >
                  <Info width={15} />
                </span>
              </label>
              <Tooltip
                id='phoneNumber'
                content='Phone number must be 10–15 digit'
              />

              <input
                id='phoneNumber'
                type='text'
                {...register(
                  'phoneNumber',
                  getPhoneValidationRules('Client Phone Number')
                )}
                onKeyDown={handlePhoneKeyDown}
                className={` ${errors.phoneNumber ? 'border-red-500' : ''}`}
                maxLength={15}
              />
              {errors.phoneNumber && (
                <p className='error-msg'>{errors.phoneNumber.message}</p>
              )}
            </div>
          </div>

          {/* <div className='p-4 border border-gray-300 rounded-lg'> */}
          <div className='input_field'>
            <label htmlFor='logoFile'>
              Upload Company Logo
              <span
                data-tooltip-id='logoFile'
                data-tooltip-content='Upload file only jpg/jpeg/png'
                className='inline-flex items-center relative top-1.5 ml-1'
              >
                <Info width={15} />
              </span>
            </label>
            <Tooltip id='logoFile' content='Upload file only jpg/jpeg/png' />

            <input
              id='logoFile'
              type='file'
              accept='image/jpeg,image/png,image/webp,image/svg+xml'
              {...register('logoFile', {
                validate: (fileList: FileList | null) => {
                  if (!fileList || fileList.length === 0) return true

                  const allowedTypes = [
                    'image/jpeg',
                    'image/png',
                    'image/webp',
                    'image/svg+xml',
                  ]

                  for (let i = 0; i < fileList.length; i++) {
                    const file = fileList.item(i)
                    if (!file) continue
                    if (!allowedTypes.includes(file.type)) {
                      return 'Only image files are allowed (jpg, png, webp, svg)'
                    }
                  }
                  return true
                },
              })}
            />

            {/* Error message */}
            {errors.logoFile && (
              <p className='error-msg'>{errors.logoFile.message}</p>
            )}

            {/* Preview */}
            {(() => {
              const logoFiles = watch('logoFile')
              if (logoFiles && logoFiles.length > 0) {
                const file = logoFiles[0]
                if (file && file.type.startsWith('image/')) {
                  return (
                    <div className='relative mt-2'>
                      <img
                        src={URL.createObjectURL(file)}
                        alt='Company Logo Preview'
                        className='signature-img'
                      />
                      <button
                        type='button'
                        className='btn-pro mt-2'
                        onClick={() => {
                          setValue('logoFile', null)
                          const input = document.getElementById(
                            'logoFile'
                          ) as HTMLInputElement | null
                          if (input) input.value = ''
                          trigger('logoFile')
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )
                }
              }
              return null
            })()}
          </div>

          <div className='input_field'>
            <label htmlFor='companyName' className=''>
              Company Name<span className='error-msg'>*</span>
              <span
                data-tooltip-id='companyName'
                data-tooltip-content='Company Name must be 2–50 characters'
                className='inline-flex items-center relative top-1.5 ml-1'
              >
                <Info width={15} />
              </span>
            </label>
            <Tooltip
              id='companyName'
              content='Company Name must be 2–50 characters'
            />

            <input
              id='companyName'
              type='text'
              {...register(
                'companyName',
                getNameValidationRules({ label: 'Company Name', maxLength: 50 })
              )}
              onKeyDown={handleTextValidation}
              className={`${errors.companyName ? 'border-red-500' : ''}`}
            />
            {errors.companyName && (
              <p className='error-msg'>{errors.companyName.message}</p>
            )}
          </div>
          {/* </div> */}

          <div className='input_field'>
            <label htmlFor='companyPhoneNumber' className=''>
              Company Phone Number<span className='error-msg'>*</span>
              <span
                data-tooltip-id='companyPhoneNumber'
                data-tooltip-content='Company Phone Number must be 10–15 digits'
                className='inline-flex items-center relative top-1.5 ml-1'
              >
                <Info width={15} />
              </span>
            </label>
            <Tooltip
              id='companyPhoneNumber'
              content='Company Phone Number must be 10–15 digits'
            />
            <input
              id='companyPhoneNumber'
              type='text'
              {...register(
                'companyPhoneNumber',
                getPhoneValidationRules('Company Phone Number')
              )}
              onKeyDown={handlePhoneKeyDown}
              className={` ${errors.companyPhoneNumber ? 'border-red-500' : ''}`}
              maxLength={15}
            />
            {errors.companyPhoneNumber && (
              <p className='error-msg'>{errors.companyPhoneNumber.message}</p>
            )}
          </div>

          <div className='input_field'>
            <label htmlFor='clientAddress' className=''>
              Client Address<span className='error-msg'>*</span>
              <span
                data-tooltip-id='clientAddress'
                data-tooltip-content='Client Address must be 10-250 characters'
                className='inline-flex items-center relative top-1.5 ml-1'
              >
                <Info width={15} />
              </span>
            </label>
            <Tooltip
              id='clientAddress'
              content='Client Address must be 10-250 characters'
            />
            <textarea
              id='clientAddress'
              {...register('clientAddress', {
                required: 'Client Address is a required',
                minLength: {
                  value: 10,
                  message: 'Client Address must be at least 10 characters',
                },
                maxLength: {
                  value: 250,
                  message: 'Client Address must not exceed 250 characters',
                },
              })}
              onKeyDown={e => {
                // Prevent first character from being a space
                const textarea = e.currentTarget
                if (e.key === ' ' && textarea.selectionStart === 0) {
                  e.preventDefault()
                }
              }}
              className={`w-full  ${
                errors.clientAddress ? 'border-red-500' : ''
              }`}
            />
            {errors.clientAddress && (
              <p className='error-msg'>{errors.clientAddress.message}</p>
            )}
          </div>

          <div className='input_field'>
            <label htmlFor='jobSiteAddress' className=''>
              Job Site Address<span className='error-msg'>*</span>
              <span
                data-tooltip-id='jobSiteAddress'
                data-tooltip-content='Job Site Address must be 10-250 characters'
                className='inline-flex items-center relative top-1.5 ml-1'
              >
                <Info width={15} />
              </span>
            </label>
            <Tooltip
              id='jobSiteAddress'
              content='Job Site Address must be 10-250 characters'
            />
            <textarea
              id='jobSiteAddress'
              {...register('jobSiteAddress', {
                required: 'Job Site Address is a required',
                minLength: {
                  value: 10,
                  message: 'Job Site Address must be at least 10 characters',
                },
                maxLength: {
                  value: 250,
                  message: 'Job Site Address must not exceed 250 characters ',
                },
              })}
              onKeyDown={e => {
                // Prevent first character from being a space
                const textarea = e.currentTarget
                if (e.key === ' ' && textarea.selectionStart === 0) {
                  e.preventDefault()
                }
              }}
              className={` ${errors.jobSiteAddress ? 'border-red-500' : ''}`}
            />
            {errors.jobSiteAddress && (
              <p className='error-msg'>{errors.jobSiteAddress.message}</p>
            )}
          </div>
          <div className='input_field'>
            <label htmlFor='state' className=''>
              State<span className='error-msg'>*</span>
              <span
                data-tooltip-id='state'
                data-tooltip-content='State must be 2-20 characters'
                className='inline-flex items-center relative top-1.5 ml-1'
              >
                <Info width={15} />
              </span>
            </label>
            <Tooltip id='state' content='State must be 2-20 characters' />
            <input
              type='text'
              id='state'
              {...register('state', getNameValidationRules({ label: 'State' }))}
              onKeyDown={handleTextValidation}
              className={` ${errors.state ? 'border-red-500' : ''}`}
            />
            {errors.state && (
              <p className='error-msg'>{errors.state.message}</p>
            )}
          </div>
          <div className='input_field'>
            <label htmlFor='city' className=''>
              City<span className='error-msg'>*</span>
              <span
                data-tooltip-id='city'
                data-tooltip-content='City must be 2-20 characters'
                className='inline-flex items-center relative top-1.5 ml-1'
              >
                <Info width={15} />
              </span>
            </label>
            <Tooltip id='city' content='City must be 2-20 characters' />
            <input
              type='text'
              id='city'
              {...register('city', getNameValidationRules({ label: 'City' }))}
              onKeyDown={handleTextValidation}
              className={` ${errors.city ? 'border-red-500' : ''}`}
            />
            {errors.city && <p className='error-msg'>{errors.city.message}</p>}
          </div>
          <div className='input_field'>
            <label htmlFor='zipcode' className=''>
              Zipcode<span className='error-msg'>*</span>
              <span
                data-tooltip-id='zipcode'
                data-tooltip-content='Use letters, numbers, or spaces only (3–10 characters)'
                className='inline-flex items-center relative top-1.5 ml-1'
              >
                <Info width={15} />
              </span>
            </label>
            <Tooltip
              id='zipcode'
              content='Use letters, numbers, or spaces only (3–10 characters)'
            />
            <input
              type='text'
              id='zipcode'
              {...register('zipcode', {
                required: 'Zipcode is required',
                pattern: {
                  value: /^[A-Za-z0-9]{3,}$/, // letters, numbers, spaces, at least 3 chars
                  message:
                    'Zipcode must be at least 3 characters and contain only letters, numbers',
                },
              })}
              maxLength={10}
              className={` ${errors.zipcode ? 'border-red-500' : ''}`}
            />
            {errors.zipcode && (
              <p className='error-msg'>{errors.zipcode.message}</p>
            )}
          </div>
          <div className='input_field'>
            <label htmlFor='startDate'>
              Start Date<span style={{ color: 'red' }}>*</span>
              <span
                data-tooltip-id='startDate'
                data-tooltip-content='Start date must be today or a future date'
                className='inline-flex items-center relative top-1.5 ml-1'
              >
                <Info width={15} />
              </span>
            </label>
            <Tooltip
              id='startDate'
              content='Start date must be today or a future date'
            />
            <Controller
              control={control}
              name='startDate'
              rules={{ required: 'Start date is required' }}
              render={({ field }) => (
                <DatePicker
                  showIcon
                  placeholderText='Select start date'
                  selected={field.value}
                  onChange={date => {
                    field.onChange(date)
                    setStartDate(date)
                  }}
                  dateFormat='yyyy-MM-dd'
                  className={`w-full ${errors.startDate ? 'border-red-500' : ''}`}
                  minDate={new Date()} // disables past dates
                />
              )}
            />

            {errors.startDate && (
              <p className='error-msg'>{errors.startDate.message}</p>
            )}
          </div>

          <div className='input_field'>
            <label htmlFor='proposalExpirationDate'>
              Approximate Date of Completion
              <span style={{ color: 'red' }}>*</span>
              <span
                data-tooltip-id='proposalExpirationDate'
                data-tooltip-content='Completion date should be later than the start date'
                className='inline-flex items-center relative top-1.5 ml-1'
              >
                <Info width={15} />
              </span>
            </label>
            <Tooltip
              id='proposalExpirationDate'
              content='Completion date should be later than the start date'
            />
            <Controller
              control={control}
              name='proposalExpirationDate'
              rules={{
                required: 'Proposal Expiration Date is required',
                validate: value => {
                  if (!startDate) return 'Please select the start date first'
                  if (value && value < startDate)
                    return 'Completion date must be after start date'
                  return true
                },
              }}
              render={({ field }) => (
                <DatePicker
                  showIcon
                  disabled={!startDate}
                  placeholderText='Select Approximate Date of Completion'
                  selected={field.value}
                  onChange={date => field.onChange(date)}
                  dateFormat='yyyy-MM-dd'
                  className={`w-full ${
                    errors.proposalExpirationDate ? 'border-red-500' : ''
                  }`}
                  minDate={startDate ?? new Date()}
                />
              )}
            />

            {errors.proposalExpirationDate && (
              <p className='error-msg'>
                {errors.proposalExpirationDate.message}
              </p>
            )}
          </div>

          <div className='input_field'>
            <label htmlFor='descriptionOfWork' className=''>
              Description of Work<span className='error-msg'>*</span>
              <span
                data-tooltip-id='descriptionOfWork'
                data-tooltip-content='Enter a brief description of the work — 20 to 500 characters'
                className='inline-flex items-center relative top-1.5 ml-1'
              >
                <Info width={15} />
              </span>
            </label>
            <Tooltip
              id='descriptionOfWork'
              content='Enter a brief description of the work — 20 to 500 character'
            />
            <textarea
              id='descriptionOfWork'
              rows={4}
              {...register('descriptionOfWork', {
                required: 'Description of Work is a required',
                minLength: {
                  value: 20,
                  message:
                    'Description of Work must be at least 20 characters ',
                },
                maxLength: {
                  value: 500,
                  message:
                    'Description of Work must not be exceed 500 character',
                },
              })}
              onKeyDown={e => {
                // Prevent first character from being a space
                const textarea = e.currentTarget
                if (e.key === ' ' && textarea.selectionStart === 0) {
                  e.preventDefault()
                }
              }}
              className={` ${errors.descriptionOfWork ? 'border-red-500' : ''}`}
            />
            {errors.descriptionOfWork && (
              <p className='error-msg'>{errors.descriptionOfWork.message}</p>
            )}
          </div>

          <div className='input_field'>
            <label htmlFor='roomdimensionFloorType' className=''>
              Room dimensions/Flooring & Materials used/Subfloor prep etc...
              <span className='error-msg'>*</span>
              <span
                data-tooltip-id='roomdimensionFloorType'
                data-tooltip-content='Provide details like dimensions, flooring type, and materials used'
                className='inline-flex items-center relative top-1.5 ml-1'
              >
                <Info width={15} />
              </span>
            </label>
            <Tooltip
              id='roomdimensionFloorType'
              content='Provide details like dimensions, flooring type, and materials used'
            />
            <textarea
              id='roomdimensionFloorType'
              rows={4}
              {...register('roomdimensionFloorType', {
                required: 'Room dimensions is a required',
                minLength: {
                  value: 20,
                  message: 'Room dimensions must be at least 20 characters',
                },
                maxLength: {
                  value: 1000,
                  message: 'Room dimensions must not be exceed 1000 character',
                },
              })}
              onKeyDown={e => {
                const textarea = e.currentTarget
                if (e.key === ' ' && textarea.selectionStart === 0) {
                  e.preventDefault()
                }
              }}
              className={` ${errors.roomdimensionFloorType ? 'border-red-500' : ''}`}
            />
            {errors.roomdimensionFloorType && (
              <p className='error-msg'>
                {errors.roomdimensionFloorType.message}
              </p>
            )}
          </div>

          <div className='input_field_col2'>
            <div className='input_field'>
              <label htmlFor='estimatAmount' className=''>
                Estimate Amount<span className='error-msg'>*</span>
                <span
                  data-tooltip-id='estimatAmount'
                  data-tooltip-content='Enter the total estimated cost for the work'
                  className='inline-flex items-center relative top-1.5 ml-1'
                >
                  <Info width={15} />
                </span>
                <Tooltip
                  id='estimatAmount'
                  content='Enter the total estimated cost for the work'
                />
              </label>
              <input
                id='estimatAmount'
                type='number'
                {...register('estimatAmount', {
                  required: 'Estimate amount is required',
                  valueAsNumber: true,
                  min: {
                    value: 1,
                    message: 'Estimate amount must be greater than zero',
                  },
                  max: {
                    value: 1000000, // Maximum value
                    message: 'Estimate amount cannot exceed 1,000,000',
                  },
                })}
                className={` ${errors.estimatAmount ? 'border-red-500' : ''}`}
              />
              {errors.estimatAmount && (
                <p className='error-msg'>{errors.estimatAmount.message}</p>
              )}
            </div>
            <div className='input_field'>
              <label htmlFor='paymentTerms' className=''>
                Payment Terms (%)<span className='error-msg'>*</span>
                <span
                  data-tooltip-id='paymentTerms'
                  data-tooltip-content='Payment Terms must be 1-100 percentage'
                  className='inline-flex items-center relative top-1.5 ml-1'
                >
                  <Info width={15} />
                </span>
                <Tooltip
                  id='paymentTerms'
                  content='Payment Terms must be 1-100 percentage'
                />
              </label>
              <input
                id='paymentTerms'
                type='number'
                step='0.1' // allow decimals like 7.5
                {...register('paymentTerms', {
                  required: 'Payment terms is required',
                  valueAsNumber: true,
                  min: {
                    value: 0,
                    message: 'Percentage must be at least 0%',
                  },
                  max: {
                    value: 100,
                    message: 'Percentage cannot exceed 100%',
                  },
                })}
                onKeyDown={handlePaymentTermDown}
                className={` ${errors.paymentTerms ? 'border-red-500' : ''}`}
              />
              {errors.paymentTerms && (
                <p className='error-msg'>{errors.paymentTerms.message}</p>
              )}
            </div>
          </div>

          {/* Contractor Signature */}
          <div className='p-4 border border-gray-300 rounded-lg'>
            {!contractSignature &&
              !((watch('contractorSignatureFile') ?? []).length > 0) && (
                <>
                  <div className='input_field'>
                    <label htmlFor='contractorSignatureFile'>
                      Upload Contractor Signature
                      <span
                        data-tooltip-id='contractorSignatureFile'
                        data-tooltip-content='Upload file only jpg/jpeg/png'
                        className='inline-flex items-center relative top-[7px] ml-1'
                      >
                        <Info width={15} />
                      </span>
                    </label>
                    <Tooltip
                      id='contractorSignatureFile'
                      content='Upload file only jpg/jpeg/png'
                    />
                    <input
                      id='contractorSignatureFile'
                      type='file'
                      accept='image/jpeg,image/png,image/webp,image/svg+xml'
                      {...register('contractorSignatureFile', {
                        validate: (fileList: FileList | null) => {
                          if (!fileList || fileList.length === 0) return true

                          const allowedTypes = [
                            'image/jpeg',
                            'image/png',
                            'image/webp',
                            'image/svg+xml',
                          ]

                          for (let i = 0; i < fileList.length; i++) {
                            const file = fileList.item(i)
                            if (!file) continue
                            if (!allowedTypes.includes(file.type)) {
                              return 'Only image files are allowed (jpg, png, webp, svg)'
                            }
                          }
                          return true
                        },
                      })}
                      className={`${errors.contractorSignatureFile ? 'border-red-500' : ''}`}
                    />
                  </div>
                  <p className='flex my-2 items-center justify-center'>OR</p>
                  {!contractSignature && (
                    <button
                      type='button'
                      className='btn-pro'
                      onClick={() => setOpenContractSignature(true)}
                    >
                      Create Contractor Signature
                      <span
                        data-tooltip-id='logoFile'
                        data-tooltip-content='Create Custom Contractor Signature'
                        className='inline-flex items-center relative top-[7px] ml-1'
                      >
                        <Info width={15} />
                      </span>
                      <Tooltip
                        id='logoFile'
                        content='Create Custom Contractor Signature'
                      />
                    </button>
                  )}
                </>
              )}

            {/* Drawn signature */}
            {contractSignature && (
              <div className='relative mt-2'>
                <img
                  className='signature-img'
                  src={contractSignature}
                  alt='contractSignature'
                />
                <button
                  type='button'
                  className='btn-pro'
                  onClick={() => setContractSignature('')}
                >
                  Remove
                </button>
              </div>
            )}

            {/* Uploaded signature */}
            {(() => {
              const contractorFiles = watch('contractorSignatureFile')
              if (contractorFiles && contractorFiles.length > 0) {
                const file = contractorFiles[0]
                if (!file) return null
                return (
                  <div className='relative mt-2'>
                    <img
                      src={URL.createObjectURL(file)}
                      alt='Uploaded Signature'
                      className='signature-img'
                    />
                    <button
                      type='button'
                      className='btn-pro'
                      onClick={() => {
                        setValue('contractorSignatureFile', null)
                        const input = document.getElementById(
                          'contractorSignatureFile'
                        ) as HTMLInputElement
                        if (input) input.value = ''
                      }}
                    >
                      Remove
                    </button>
                  </div>
                )
              }
              return null
            })()}

            {errors.contractorSignatureFile && (
              <p className='error-msg'>
                {errors.contractorSignatureFile.message}
              </p>
            )}

            <SignaturePad
              isOpen={openContractSignature}
              title='Create Contractor Signature'
              setImageURL={url => setContractSignature(url)}
              closeSignaturePad={() => setOpenContractSignature(false)}
            />
          </div>

          <div className='p-4 border border-gray-300 rounded-lg'>
            {!customerSignature &&
              !((watch('customerSignatureFile') ?? []).length > 0) && (
                <>
                  <div className='input_field'>
                    <label htmlFor='customerSignatureFile'>
                      Upload Customer Signature
                      <span
                        data-tooltip-id='customerSignatureFile'
                        data-tooltip-content='Upload file only jpg/jpeg/png'
                        className='inline-flex items-cente relative top-1 ml-1'
                      >
                        <Info width={15} />
                      </span>
                    </label>
                    <Tooltip
                      id='customerSignatureFile'
                      content='Upload file only jpg/jpeg/png'
                    />
                    <input
                      id='customerSignatureFile'
                      type='file'
                      accept='image/jpeg,image/png,image/webp,image/svg+xml'
                      {...register('customerSignatureFile', {
                        validate: (fileList: FileList | null) => {
                          if (!fileList || fileList.length === 0) return true

                          const allowedTypes = [
                            'image/jpeg',
                            'image/png',
                            'image/webp',
                            'image/svg+xml',
                          ]

                          for (let i = 0; i < fileList.length; i++) {
                            const file = fileList.item(i)
                            if (!file) continue
                            if (!allowedTypes.includes(file.type)) {
                              return 'Only image (jpg, png, webp, svg) or PDF files are allowed'
                            }
                          }
                          return true
                        },
                      })}
                      className={`${errors.customerSignatureFile ? 'border-red-500' : ''}`}
                    />
                  </div>

                  <p className='flex my-2 items-center justify-center'>OR</p>
                  <button
                    type='button'
                    className='btn-pro'
                    onClick={() => setOpenCustomerSignature(true)}
                  >
                    Create Customer Signature
                    <span
                      data-tooltip-id='logoFile'
                      data-tooltip-content=' Create Custom Customer Signature'
                      className='inline-flex items-center relative top-1.5 ml-1'
                    >
                      <Info width={15} />
                    </span>
                    <Tooltip
                      id='logoFile'
                      content=' Create Custom Customer Signature'
                    />
                  </button>
                </>
              )}

            {/* Drawn signature */}
            {customerSignature && (
              <div className='relative mt-2'>
                <img
                  className='signature-img'
                  src={customerSignature}
                  alt='customerSignature'
                />

                <button
                  type='button'
                  className='btn-pro'
                  onClick={() => setCustomerSignature('')}
                >
                  Remove
                </button>
              </div>
            )}

            {/* Uploaded signature preview */}
            {(() => {
              const customerFiles = watch('customerSignatureFile')
              if (customerFiles && customerFiles.length > 0) {
                const file = customerFiles[0]
                if (!file) return null
                return (
                  <div className='relative mt-2'>
                    {file.type.startsWith('image/') ? (
                      <img
                        src={URL.createObjectURL(file)}
                        alt='Uploaded Signature'
                        className='signature-img'
                      />
                    ) : (
                      <p className='text-green-600'>
                        Uploaded file: {file.name}
                      </p>
                    )}
                    <button
                      type='button'
                      className='btn-pro'
                      onClick={() => {
                        setValue('customerSignatureFile', null)
                        const input = document.getElementById(
                          'customerSignatureFile'
                        ) as HTMLInputElement
                        if (input) input.value = ''
                      }}
                    >
                      Remove
                    </button>
                  </div>
                )
              }
              return null
            })()}

            {errors.customerSignatureFile && (
              <p className='error-msg'>
                {errors.customerSignatureFile.message}
              </p>
            )}

            <SignaturePad
              isOpen={opencustomerSignature}
              title='Create Customer Signature'
              setImageURL={url => setCustomerSignature(url)}
              closeSignaturePad={() => setOpenCustomerSignature(false)}
            />
          </div>
        </div>
        <div className='btn-wrap flex justify-center'>
          <button
            type='submit'
            disabled={isLoading}
            className={`px-6 py-2 rounded-md font-semibold text-white transition-pro ${
              isLoading
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isLoading ? <Loader /> : 'Generate PDF'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default PdfForm
